import * as Sentry from "@sentry/react";
import L, { type Map as LeafletMap, type Polyline } from "leaflet";
import {
  GeomType,
  PmtilesSource,
  TileCache,
  type Zxy,
} from "protomaps-leaflet";
import {
  COVERAGE_STROKE_PANE_NAME,
  COVERAGE_STROKE_PANE_CLASS,
} from "../constants/pane";
import { isSignedUrlExpiredForbiddenError } from "./trustartTileSignedUrl";

// =============================================================================
// 役所地番地図の対応エリア枠線（ベクタ描画）
//
// 枠線をタイルの canvas に描くと、ズーム遷移中に旧ズームタイルの CSS 拡大縮小で
// 線の見かけの太さが変わる／描き直しまで線が消える、というチカチカが原理的に
// 避けられない。そこで枠線だけは pmtiles からジオメトリを取り出し、Leaflet の
// SVG ベクタとして描画する。ベクタはズームアニメーション中に地図と一緒に
// スケールし、ズーム確定と同時に Leaflet が既存パスを正しい太さのまま再投影する
// ため、線が消える瞬間も太さが脈打つ瞬間もない。ズーム確定後にそのズームに
// 適した詳細度のジオメトリを非同期で取り直し、出来上がってから入れ替える
// （入れ替えまでは1つ前のズームのジオメトリがそのまま表示され続ける）。
// =============================================================================

/** タイルローカル座標の1辺(px)。PmtilesSource.get に渡すスケールと一致させる */
const TILE_SIZE = 256;

// 対応エリアがタイルに焼かれている最大データズーム（tippecanoe 側の設定と一致させる）
const COVERAGE_MAX_DATA_ZOOM = 13;

// 1回の更新で読むタイル数の上限（暴走防止）。超える場合は諦めるのではなく
// データズームを1段ずつ下げて収める。大きな地図描画領域（高解像度ディスプレイや
// ブラウザのズームアウト）では上限を超え続けるため、諦めると枠線が二度と更新されず
// 古い形状が残ったままになる。
const MAX_TILES_PER_UPDATE = 64;

// 地図出力（mapExportVisibility）でこのペインを識別するためにも使われる。
// Leaflet はペイン名から "leaflet-<name>-pane" のクラスを付与する
export { COVERAGE_STROKE_PANE_NAME, COVERAGE_STROKE_PANE_CLASS };

// tilePane 配下に作る子ペインの zIndex。TRUSTART_CHIBAN(13) の直上に置き、
// データレイヤー群（zIndex 100+）よりは下にする
const PANE_Z_INDEX = "14";

export interface CoverageStrokeStyle {
  /** 線色。不透明で描き、透過は opacity（ペイン）で掛ける */
  color: string;
  /** 線幅(px) */
  weight: number;
  /** ペイン全体に掛ける不透明度 */
  opacity: number;
  /** 表示ズーム範囲（display zoom・両端 inclusive） */
  minZoom: number;
  maxZoom: number;
}

interface LocalPoint {
  x: number;
  y: number;
}

/**
 * fetch の中断によるエラーか。PmtilesSource を shouldCancelZooms=true で作っているため、
 * ズームが変わると protomaps が前ズームの取得を abort する（正常系）。
 */
const isAbortError = (error: unknown): boolean =>
  error instanceof Error && error.name === "AbortError";

/**
 * 線分を [0, size] の正方形に Liang-Barsky 法でクリップする。
 * 完全に外側なら null。
 */
const clipSegment = (
  ax: number,
  ay: number,
  bx: number,
  by: number,
  size: number
): [number, number, number, number] | null => {
  const dx = bx - ax;
  const dy = by - ay;
  const p = [-dx, dx, -dy, dy];
  const q = [ax, size - ax, ay, size - ay];
  let t0 = 0;
  let t1 = 1;
  for (let i = 0; i < 4; i++) {
    if (p[i] === 0) {
      if (q[i] < 0) return null;
    } else {
      const r = q[i] / p[i];
      if (p[i] < 0) {
        if (r > t1) return null;
        if (r > t0) t0 = r;
      } else {
        if (r < t0) return null;
        if (r < t1) t1 = r;
      }
    }
  }
  return [ax + t0 * dx, ay + t0 * dy, ax + t1 * dx, ay + t1 * dy];
};

/**
 * ポリゴンのリング（閉じた点列）をタイル矩形にクリップし、連続する線分ごとの
 * 点列（ポリライン）にして返す。
 *
 * ポリゴンとしてではなく線分単位でクリップするのがポイントで、こうすると
 * tippecanoe がタイル化時に付けたクリップ辺（タイル境界のバッファ上を走る
 * 人工的な辺）はタイル矩形の外側にあるため丸ごと落ち、本物の境界線だけが残る。
 * タイルをまたぐ境界線は隣のタイルが続きを描くので切れ目なく繋がる。
 */
const clipRingToTile = (ring: LocalPoint[], size: number): LocalPoint[][] => {
  const runs: LocalPoint[][] = [];
  let current: LocalPoint[] | null = null;
  for (let i = 0; i < ring.length - 1; i++) {
    const a = ring[i];
    const b = ring[i + 1];
    const seg = clipSegment(a.x, a.y, b.x, b.y, size);
    if (seg === null) {
      current = null;
      continue;
    }
    const [sx, sy, ex, ey] = seg;
    // 始点が切られていなければ直前の線分と連続している
    if (current !== null && sx === a.x && sy === a.y) {
      current.push({ x: ex, y: ey });
    } else {
      current = [
        { x: sx, y: sy },
        { x: ex, y: ey },
      ];
      runs.push(current);
    }
    // 終点が切られたら次の線分とは不連続
    if (ex !== b.x || ey !== b.y) {
      current = null;
    }
  }
  return runs;
};

/**
 * 対応エリアの枠線を SVG ベクタで描く Leaflet オーバーレイ。
 *
 * - ジオメトリは表示ズーム-1（protomaps の levelDiff=1 と同じ）のデータタイルから
 *   取り出すので、ラスタ側の塗り（coverageFillLayer）と輪郭が一致する。
 * - ポリラインは不透明で描き、透過は専用ペインの CSS opacity で一括適用する。
 *   隣接ポリゴンが共有する境界線が二重に描かれても濃度が変わらない。
 * - moveend（ズーム確定・パン完了）でビューポート分のタイルを取り直す。
 *   取得完了までは古いラインが表示され続けるため、線が消える瞬間がない。
 */
export class TrustartChibanCoverageStrokeOverlay {
  private readonly map: LeafletMap;
  private readonly cache: TileCache;
  // 取得失敗が署名切れ由来か判定するために URL を保持する（PmtilesSource は URL を公開しない）
  private readonly pmtilesUrl: string;
  private readonly dataLayerName: string;
  private readonly style: CoverageStrokeStyle;
  private readonly group = L.layerGroup();
  private renderer: L.SVG | null = null;
  private added = false;
  // 非同期更新の競合を捨てるための世代カウンタ
  private generation = 0;

  constructor(
    map: LeafletMap,
    pmtilesUrl: string,
    dataLayerName: string,
    style: CoverageStrokeStyle
  ) {
    this.map = map;
    this.cache = new TileCache(new PmtilesSource(pmtilesUrl, true), TILE_SIZE);
    this.pmtilesUrl = pmtilesUrl;
    this.dataLayerName = dataLayerName;
    this.style = style;
  }

  add(): void {
    if (this.added) return;
    this.added = true;
    let pane = this.map.getPane(COVERAGE_STROKE_PANE_NAME);
    if (pane === undefined) {
      // tilePane の子にすることでタイルレイヤー同士の zIndex 序列に割り込める
      pane = this.map.createPane(
        COVERAGE_STROKE_PANE_NAME,
        this.map.getPane("tilePane")
      );
      pane.style.zIndex = PANE_Z_INDEX;
      pane.style.pointerEvents = "none";
    }
    pane.style.opacity = String(this.style.opacity);
    this.renderer = L.svg({ pane: COVERAGE_STROKE_PANE_NAME });
    this.group.addTo(this.map);
    this.map.on("moveend", this.handleMoveEnd);
    void this.update();
  }

  remove(): void {
    if (!this.added) return;
    this.added = false;
    this.map.off("moveend", this.handleMoveEnd);
    this.generation++;
    this.group.clearLayers();
    this.group.remove();
    this.renderer?.remove();
    this.renderer = null;
  }

  private readonly handleMoveEnd = (): void => {
    void this.update();
  };

  /** 現在のビューポートに必要なデータタイルを取得してラインを組み直す */
  private async update(): Promise<void> {
    const generation = ++this.generation;
    const zoom = Math.round(this.map.getZoom());
    if (zoom < this.style.minZoom || zoom > this.style.maxZoom) {
      this.group.clearLayers();
      return;
    }
    // ラスタ側（protomaps の levelDiff=1）と同じデータズームを参照する。
    // ただしタイル数が上限を超える場合は収まるまで下げる（形状は粗くなるが、
    // unproject にも同じ dataZoom を使うので位置はずれない）。
    let dataZoom = Math.max(0, Math.min(zoom - 1, COVERAGE_MAX_DATA_ZOOM));
    let tiles = this.tilesInView(dataZoom);
    while (tiles.length > MAX_TILES_PER_UPDATE && dataZoom > 0) {
      dataZoom--;
      tiles = this.tilesInView(dataZoom);
    }
    if (tiles.length === 0) {
      this.group.clearLayers();
      return;
    }

    // 取得失敗（通信断・pmtiles の破損など）は枠線が黙って欠けるだけになるので、
    // 検知できるよう控えめに通知する。ただし次の2つは想定内なので通知しない。
    // - 中断: ズーム変更で protomaps が前ズームの取得を abort する正常系
    // - 署名切れの403: useTrustartTile が URL を再取得し、新しい URL でレイヤーが
    //   作り直されて自動的に回復する。SWR の再取得タイマーはタブが背面／端末が
    //   スリープの間は動かないため、復帰直後に必ず一度は発生する（LAKERS-FRONTEND-RD）。
    //   署名がまだ有効なうちの403（署名鍵の失効・配信設定ミス）は回復しないので通知する。
    const failures: unknown[] = [];
    const results = await Promise.all(
      tiles.map(
        async (c) =>
          await this.cache.get(c).then(
            (data) => ({ c, data }),
            // 取得に失敗したタイルだけ諦める（他のタイルの描画は続行する）
            (error: unknown) => {
              if (
                !isAbortError(error) &&
                !isSignedUrlExpiredForbiddenError(error, this.pmtilesUrl)
              ) {
                failures.push(error);
              }
              return null;
            }
          )
      )
    );
    if (failures.length > 0) {
      // 同一原因でタイル数ぶん発生するため、1回の更新につき1件だけ送る
      Sentry.captureException(failures[0], {
        tags: { feature: "trustart-chiban-coverage-stroke" },
        extra: {
          failedTiles: failures.length,
          requestedTiles: tiles.length,
          dataZoom,
        },
      });
    }
    if (generation !== this.generation || !this.added) return;

    const polylines: Polyline[] = [];
    for (const result of results) {
      if (result === null) continue;
      const features = result.data.get(this.dataLayerName) ?? [];
      for (const feature of features) {
        if (feature.geomType !== GeomType.Polygon) continue;
        for (const ring of feature.geom) {
          for (const run of clipRingToTile(ring, TILE_SIZE)) {
            const latlngs = run.map((p) =>
              this.map.unproject(
                L.point(
                  result.c.x * TILE_SIZE + p.x,
                  result.c.y * TILE_SIZE + p.y
                ),
                dataZoom
              )
            );
            polylines.push(
              L.polyline(latlngs, {
                pane: COVERAGE_STROKE_PANE_NAME,
                renderer: this.renderer ?? undefined,
                color: this.style.color,
                weight: this.style.weight,
                opacity: 1, // 透過はペインで一括適用する
                fill: false,
                interactive: false,
              })
            );
          }
        }
      }
    }

    // 新しいラインを足してから古いラインを外す（入れ替えの一瞬も線を消さない。
    // 重なった瞬間はペイン opacity が合成を一定に保つ）
    const stale = this.group.getLayers();
    polylines.forEach((line) => {
      this.group.addLayer(line);
    });
    stale.forEach((layer) => {
      this.group.removeLayer(layer);
    });
  }

  /** 現在のビューポートを覆うデータズームのタイル一覧 */
  private tilesInView(dataZoom: number): Zxy[] {
    const bounds = this.map.getBounds();
    const min = this.map
      .project(bounds.getNorthWest(), dataZoom)
      .divideBy(TILE_SIZE)
      .floor();
    const max = this.map
      .project(bounds.getSouthEast(), dataZoom)
      .divideBy(TILE_SIZE)
      .floor();
    const maxIndex = Math.pow(2, dataZoom) - 1;
    const tiles: Zxy[] = [];
    for (let x = Math.max(0, min.x); x <= Math.min(maxIndex, max.x); x++) {
      for (let y = Math.max(0, min.y); y <= Math.min(maxIndex, max.y); y++) {
        tiles.push({ z: dataZoom, x, y });
      }
    }
    return tiles;
  }
}
